/** Bordered scroll host for data tables (admin lists). */
export const DATA_TABLE_SCROLL =
  "overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/[0.08] dark:bg-surface dark:shadow-surface-dark";

/** Loading / empty / error placeholder inside the scroll host. */
export const DATA_TABLE_STATE_PANEL =
  "flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 dark:border-white/[0.08] dark:bg-surface-elevated/80";

/** Bar below the table with page controls. */
export const DATA_TABLE_PAGINATION_BAR =
  "rounded-xl border border-gray-200 bg-white px-3 py-2 pb-4 shadow-sm dark:border-white/[0.08] dark:bg-surface dark:shadow-surface-dark";

export const DATA_TABLE_HEADER_ROW =
  "border-b border-gray-200 bg-gray-50 dark:border-white/[0.08] dark:bg-white/[0.04]";

export const DATA_TABLE_BODY_ROW =
  "border-b border-gray-100 transition-colors last:border-b-0 hover:bg-gray-50/80 dark:border-white/[0.06] dark:hover:bg-white/[0.03]";

/** Standard `<table>` — auto layout keeps headers/body columns aligned. */
export const DATA_TABLE_TABLE_CLASS = "w-full min-w-[640px] table-auto border-collapse";

/** Minimum width for status/badge columns. */
export const DATA_TABLE_COMPACT_COL_CLASS = "min-w-[6.5rem] w-[6.5rem] whitespace-nowrap";

/** Minimum width for the actions column (header + body must match). */
export const DATA_TABLE_ACTIONS_COL_CLASS = "min-w-[8.5rem] w-[8.5rem] whitespace-nowrap";

export type DataTableCellOptions = {
  /** Center-align numeric columns (balance, counts). */
  numeric?: boolean;
  /** Truncate long text with title tooltip (only when explicitly needed). */
  truncate?: boolean;
  /** Narrow column for status badges. */
  compact?: boolean;
  /** Actions column — fixed width. */
  actions?: boolean;
};

export function dataTableHeaderCellClass(
  isArabic: boolean,
  sortable = true,
  opts?: Pick<DataTableCellOptions, "numeric" | "compact" | "actions">
): string {
  let align: string;
  if (opts?.numeric) {
    align = "text-center";
  } else if (opts?.compact) {
    align = isArabic ? "text-right" : "text-left";
  } else {
    align = isArabic ? "text-right" : "text-left";
  }
  return [
    "px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400",
    align,
    sortable ? "cursor-pointer select-none" : "",
    opts?.actions
      ? DATA_TABLE_ACTIONS_COL_CLASS
      : opts?.compact
        ? DATA_TABLE_COMPACT_COL_CLASS
        : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function dataTableBodyCellClass(
  isArabic: boolean,
  opts?: DataTableCellOptions
): string {
  let align: string;
  if (opts?.numeric) {
    align = "text-center tabular-nums";
  } else {
    align = isArabic ? "text-right" : "text-left";
  }
  const width = opts?.actions
    ? DATA_TABLE_ACTIONS_COL_CLASS
    : opts?.compact
      ? DATA_TABLE_COMPACT_COL_CLASS
      : "";
  const truncate = opts?.truncate
    ? "max-w-[20rem] overflow-hidden text-ellipsis whitespace-nowrap"
    : "";
  return [
    "px-4 py-3 text-sm font-medium text-gray-700 dark:text-slate-100 align-middle",
    align,
    width,
    truncate,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Actions column — fixed width so the menu button stays inside the cell. */
export function dataTableActionsCellClass(isArabic: boolean): string {
  return [
    DATA_TABLE_ACTIONS_COL_CLASS,
    "px-3 py-3 align-middle",
    isArabic ? "text-right" : "text-right",
  ].join(" ");
}

/** Primary button for list page headers. */
export const LIST_PAGE_PRIMARY_BTN =
  "rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600 dark:hover:bg-brand-600";
